import { Calendar as PrimeCalendar } from 'primereact/calendar';
import {
  FC,
  useEffect,
  useId,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { Nullable } from 'primereact/ts-helpers';
import { useFormatDate } from '@/hooks/useFormatDate';
import { CalendarProps } from './Calendar.types';
import { Button } from '@/components/Common';
import nextIcon from '@/assets/icons/next.svg';
import prevIcon from '@/assets/icons/prev.svg';

import { useCustomFilterStore } from '@/store/CustomFilterStore/CustomFilter';

export const Calendar: FC<CalendarProps> = ({
  label,
  name,
  helpText,
  placeholder,
  labelStyle,
  errorStyle,
  setValue,
  minDate,
  maxDate,
  showIcon = false,
  dateFormat = 'dd/mm/yy',
  view = 'date',
  className,
  type = 'single',
  dateIcon,
  mode,
  showTime = false,
  hourFormat = '24',
  timeOnly = false,
  defaultValue,
  viewLabel,
}) => {
  const calendarRef = useRef<PrimeCalendar>(null);
  const { formatDate, formatDateYear } = useFormatDate();
  const id = useId();
  const [selectedDate, setSelectedDate] = useState<Nullable<Date | Date[]>>(
    defaultValue || null,
  );
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const {
    setSelectedDateRange,
    selectedDateRange,
    applyFilters,
    setIsFilterApplied,
  } = useCustomFilterStore();

  useEffect(() => {
    if (defaultValue && name && setValue) {
      setValue(name, defaultValue, { shouldValidate: true });
      if (type === 'range' && Array.isArray(defaultValue)) {
        setSelectedDateRange(defaultValue);
      } else {
        setSelectedDate(defaultValue);
      }
    }
  }, [defaultValue, name, setValue, type, setSelectedDateRange]);

  const handleDateChange = useCallback(
    (date: Date | Date[] | null) => {
      if (type === 'range') {
        if (Array.isArray(date)) {
          setSelectedDateRange(date);
        }
      } else {
        setSelectedDate(date);
      }
      if (name && setValue) {
        setValue(name, date, { shouldValidate: true });
      }
    },
    [name, setValue, setSelectedDateRange, type],
  );

  const handleFilter = useCallback(() => {
    applyFilters();
    if (calendarRef.current) {
      calendarRef.current.hide();
      setIsCalendarOpen(false);
    }
  }, [applyFilters]);

  const toggleCalendar = useCallback(() => {
    if (calendarRef.current) {
      if (isCalendarOpen) {
        calendarRef.current.hide();
      } else {
        calendarRef.current.show();
      }
      setIsCalendarOpen((prev) => !prev); // Inverser l'état d'ouverture/fermeture
    }
  }, [isCalendarOpen]);

  useEffect(() => {
    if (selectedDateRange) {
      setIsFilterApplied(false);
    }
  }, [selectedDateRange, setIsFilterApplied]);

  const renderDateRangeFooter = useMemo(
    () => (
      <div className="p-4 pt-7">
        <div className="flex items-center gap-4">
          <DateInput
            date={(selectedDateRange as Date[])?.[0]}
            placeholder="Date début"
            formatDate={formatDate}
            dateIcon={dateIcon}
          />
          <span className="text-[#ADADB1]">Au</span>
          <DateInput
            date={(selectedDateRange as Date[])?.[1]}
            placeholder="Date fin"
            formatDate={formatDate}
            dateIcon={dateIcon}
          />
        </div>
        <Button
          title="Filtrer"
          className="w-full mt-11"
          onClick={handleFilter}
        />
      </div>
    ),
    [selectedDateRange, formatDate, handleFilter],
  );

  if (type === 'range') {
    return (
      <div className="h-11 max-w-sm mx-auto text-white rounded-lg">
        <div
          className="w-[200px] h-[43px] rounded-md bg-almostBlack flex items-center justify-between px-3 cursor-pointer"
          onClick={toggleCalendar}
        >
          <label className="text-sm text-baseGrey cursor-pointer">
            {label}
          </label>
          <span className="text-white">
            <img src={dateIcon} alt="date icon" />
          </span>
        </div>
        <PrimeCalendar
          ref={calendarRef}
          inputId={`${id}-${name}`}
          value={selectedDateRange as Date[]}
          onChange={(e) => handleDateChange(e.value as Date[])}
          selectionMode="range"
          dateFormat={dateFormat}
          nextIcon={<img src={nextIcon} alt="next" />}
          prevIcon={<img src={prevIcon} alt="prev" />}
          className="mt-1 p-2 rounded-md opacity-0"
          panelClassName="!bg-[#232323] text-[#ADADB1] relative !top-[70px] !left-[318px] z-10 !mt-0 !pt-[10px]"
          footerTemplate={() => renderDateRangeFooter}
          showTime={showTime}
          hourFormat={hourFormat}
          timeOnly={timeOnly}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow gap-x-2 w-full min-h-12">
      {viewLabel && (
        <label className="text-white text-sm mb-2">{viewLabel}</label>
      )}
      <div
        className={`relative rounded-[4.2px] min-h-12 ${selectedDate ? 'text-white' : ''} ${className}`}
      >
        <div className="absolute inset-0 flex items-center">
          {label && (
            <div className="flex items-center justify-start gap-4 w-full px-3">
              <span className="text-[#939393]">
                <img src={dateIcon} alt="date icon" />
              </span>
              <label
                className="cursor-pointer"
                htmlFor={`${id}-${name}`}
                style={{
                  ...labelStyle,
                  color:
                    selectedDate && !mode ? '#fff' : mode ? '#fff' : '#939393',
                }}
              >
                {selectedDate === null
                  ? label
                  : dateFormat === 'yy'
                    ? formatDateYear(selectedDate as Date)
                    : formatDate(selectedDate as Date)}
              </label>
            </div>
          )}
          {showIcon && (
            <div className="absolute right-5 text-white">
              <i className="pi pi-chevron-down" />{' '}
            </div>
          )}
        </div>
        <PrimeCalendar
          inputId={`${id}-${name}`}
          value={selectedDate as Date}
          onChange={(e) => handleDateChange(e.value as Date)}
          dateFormat={dateFormat}
          view={view}
          placeholder={placeholder}
          showIcon={showIcon}
          className="opacity-0 w-full h-full cursor-pointer"
          minDate={minDate}
          maxDate={maxDate}
          panelClassName="!bg-[#232323] text-[#ADADB1]"
          showTime={showTime}
          hourFormat={hourFormat}
          timeOnly={timeOnly}
        />
      </div>
      <div className={`text-[12.8px] pt-2 font-raleway ${errorStyle}`}>
        {helpText}
      </div>
    </div>
  );
};

const DateInput: FC<{
  date: Date | undefined;
  placeholder: string;
  dateIcon: string;
  formatDate: (date: Date) => string;
}> = ({ date, placeholder, formatDate, dateIcon }) => (
  <div className="flex flex-col">
    <div className="flex items-center justify-between rounded-xl px-3 bg-bg w-[148px] h-9 text-[11px] text-[#939393]">
      <span>{date ? formatDate(date) : placeholder}</span>
      <img src={dateIcon} alt="date" className="w-4 h-4" />
    </div>
  </div>
);
